import {
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  MixpanelSourceProvider,
  Tour,
  useMixpanel,
  useSecuritiesContext,
  VStack,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo } from 'react';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useTradingSettings } from '../../../../providers/TradingSettingsContext';
import { TourName } from '../../../../tokens/tours';
import type { PriceGridV2PanelConfig } from '../../../../types/LayoutConfig';
import { expandSecurityToPriceGridRows } from '../../expandSecurityToPriceGridRows';
import { usePriceGridDataObservable, usePriceGridFilter } from '../../hooks';
import { usePriceGridMarketPriceAction } from '../../hooks/actions/primeOrder/usePriceGridMarketPriceAction';
import { usePriceGridSettings } from '../../hooks/usePriceGridSettings';
import { usePriceGridSourceObservable } from '../../hooks/usePriceGridSourceObservable';
import {
  clampPriceIncrement,
  getLadderType,
  getMinPriceIncrement,
  getPriceIncrement,
  PriceGridLadderType,
  setLadderType,
  setPriceIncrement,
} from '../../ladder';
import { PriceGrid } from '../../PriceGrid';
import { PriceGridBody } from '../../PriceGridHeaderLayout';
import { PriceGridSettingsContext } from '../../PriceGridSettingsContext';
import type { PriceGridFilterConfig, PriceGridSourceItem } from '../../types';
import { getMultilegPriceGridColumns } from './columns';
import { MULTILEG_PRICE_GRID_FILTER_CONFIG } from './filters';
import { MultilegPriceGridHeader } from './MultilegPriceGridHeader';

export interface MultilegPriceGridProps {
  blotterID: string;
  filterConfig?: PriceGridFilterConfig<PriceGridSourceItem>;
  parameters?: Pick<PriceGridV2PanelConfig, 'filterState'>;
  onParametersChange?: (parameters: Partial<Pick<PriceGridV2PanelConfig, 'filterState'>>) => void;
}

export function MultilegPriceGrid({
  blotterID,
  filterConfig = MULTILEG_PRICE_GRID_FILTER_CONFIG,
  parameters,
  onParametersChange,
}: MultilegPriceGridProps) {
  const mixpanel = useMixpanel(MixpanelEventSource.MultilegPriceGrid);
  const filtersPortalId = `${blotterID}-multileg-price-grid-filters`;
  const sourceObservable = usePriceGridSourceObservable();
  const { securitiesListSortedByRank } = useSecuritiesContext();
  const { enableInlineMultilegMarketEditing } = useFeatureFlag();
  const { useNewMarketSelector } = useTradingSettings();
  const { filterState, setFilterState, setSearch, filterSourceItems } = usePriceGridFilter({
    filterConfig,
    filterState: parameters?.filterState,
    onFilterStateChanged: nextFilterState => onParametersChange?.({ filterState: nextFilterState }),
  });
  const ladderType = getLadderType(filterState);
  const priceIncrement = getPriceIncrement(filterState);
  const filteredSecurities = useMemo(
    () =>
      filterSourceItems(securitiesListSortedByRank.map(security => ({ security }))).map(
        sourceItem => sourceItem.security
      ),
    [filterSourceItems, securitiesListSortedByRank]
  );
  const minPriceIncrement = useMemo(() => getMinPriceIncrement(filteredSecurities), [filteredSecurities]);
  const resolvedPriceIncrement = useMemo(
    () => clampPriceIncrement(priceIncrement, minPriceIncrement),
    [minPriceIncrement, priceIncrement]
  );

  useEffect(() => {
    if (resolvedPriceIncrement !== priceIncrement) {
      setFilterState(setPriceIncrement(filterState, resolvedPriceIncrement));
    }
  }, [filterState, priceIncrement, resolvedPriceIncrement, setFilterState]);

  const expandRows = useCallback(
    (sourceItem: PriceGridSourceItem) => expandSecurityToPriceGridRows(sourceItem, { ladderType }),
    [ladderType]
  );
  const dataObservable = usePriceGridDataObservable({
    sourceObservable,
    filterSourceItems,
    expandRows,
  });
  const { orderPresetID, subAccount } = filterState;
  const ladderConfig = useMemo(
    () => ({ ladderType, priceIncrement: resolvedPriceIncrement }),
    [ladderType, resolvedPriceIncrement]
  );
  const { clickToTradePreviewOrder, maxOrderSizeDialogs, onCancelClickToTradePreview, onPrimeOrderClick } =
    usePriceGridMarketPriceAction({ orderPresetID, subAccount }, ladderConfig.priceIncrement);
  const priceGridMarketDataHandlers = useMemo(
    () => ({
      onRFQClick: () => {},
      onPrimeOrderClick,
      onCancelClickToTradePreview,
    }),
    [onCancelClickToTradePreview, onPrimeOrderClick]
  );
  const priceGridSettings = usePriceGridSettings(filterState, setFilterState, MixpanelEventSource.MultilegPriceGrid);
  const columns = useMemo(
    () =>
      getMultilegPriceGridColumns({
        handlers: priceGridMarketDataHandlers,
        isBBOEnabled: false,
        ladderConfig,
        showLegMarketSelector: priceGridSettings.showLegMarketSelector,
      }),
    [ladderConfig, priceGridMarketDataHandlers, priceGridSettings.showLegMarketSelector]
  );
  const gridOptions = useMemo(
    () => ({
      maintainColumnOrder: false,
    }),
    []
  );

  const handleLadderTypeChange = useCallback(
    (nextLadderType: typeof ladderType) => {
      if (nextLadderType === ladderType) {
        return;
      }

      mixpanel.track(
        nextLadderType === PriceGridLadderType.Book
          ? MixpanelEvent.TogglePriceGridLadderToPrice
          : MixpanelEvent.TogglePriceGridLadderToVolume,
        {
          [MixpanelEventProperty.Source]: MixpanelEventSource.MultilegPriceGrid,
        }
      );
      setFilterState(setLadderType(filterState, nextLadderType));
    },
    [filterState, ladderType, mixpanel, setFilterState]
  );

  const handlePriceIncrementChange = useCallback(
    (increment: string) => {
      const clampedIncrement = clampPriceIncrement(increment, minPriceIncrement);
      if (clampedIncrement === resolvedPriceIncrement) {
        return;
      }

      mixpanel.track(MixpanelEvent.ChangePriceGridLadderIncrement, {
        [MixpanelEventProperty.Value]: clampedIncrement,
        [MixpanelEventProperty.Source]: MixpanelEventSource.MultilegPriceGrid,
      });
      setFilterState(setPriceIncrement(filterState, clampedIncrement));
    },
    [filterState, minPriceIncrement, mixpanel, resolvedPriceIncrement, setFilterState]
  );

  const handleSearchChange = useCallback(
    (search: string) => {
      mixpanel.track(MixpanelEvent.FilterQuickSearch, {
        [MixpanelEventProperty.Value]: search,
        [MixpanelEventProperty.Source]: MixpanelEventSource.MultilegPriceGrid,
      });
      setSearch(search);
    },
    [mixpanel, setSearch]
  );

  return (
    <VStack h="100%" w="100%" alignItems="flex-start" data-testid="price-grid-showcase-panel">
      {priceGridSettings.showLegMarketSelector && useNewMarketSelector && enableInlineMultilegMarketEditing ? (
        <Tour tour={TourName.v120PriceGridMultilegQuickMarketSelector} delay={1000} />
      ) : null}
      <MultilegPriceGridHeader
        filtersPortalId={filtersPortalId}
        filterState={filterState}
        ladderType={ladderType}
        minPriceIncrement={minPriceIncrement}
        onFilterStateChanged={setFilterState}
        onLadderTypeChange={handleLadderTypeChange}
        onPriceIncrementChange={handlePriceIncrementChange}
        priceIncrement={resolvedPriceIncrement}
      />
      <PriceGridBody>
        <MixpanelSourceProvider value={MixpanelEventSource.MultilegPriceGrid}>
          <PriceGridSettingsContext.Provider value={priceGridSettings}>
            <PriceGrid
              blotterID={blotterID}
              dataObservable={dataObservable}
              columns={columns}
              filtersPortalId={filtersPortalId}
              search={filterState.search}
              onSearchChange={handleSearchChange}
              supportsFavorites
              supportsMarketDataColumnSettings={false}
              priceGridMarketDataHandlers={priceGridMarketDataHandlers}
              clickToTradePreviewOrder={clickToTradePreviewOrder}
              ladderConfig={ladderConfig}
              gridOptions={gridOptions}
              maxOrderSizeDialogs={maxOrderSizeDialogs}
              showBBOToggle={false}
              showRFQToggle={false}
              showLegMarketSelectorToggle
            />
          </PriceGridSettingsContext.Provider>
        </MixpanelSourceProvider>
      </PriceGridBody>
    </VStack>
  );
}
